<template>
  <div
    class="app_shell"
    :class="{
      'app_shell--collapsed': collapsed && !isMobile,
      'app_shell--mobile': isMobile
    }"
  >
    <v-navigation-drawer
      v-if="canShowShell"
      v-model="drawerVisible"
      fixed
      app
      :temporary="isMobile"
      :clipped="false"
      :permanent="!isMobile"
      :width="sidebarWidth"
      class="app_shell__drawer"
      :class="{
        'app_shell__drawer--collapsed': collapsed && !isMobile,
        'app_shell__drawer--mobile': isMobile
      }"
    >
      <app-sidebar
        ref="sidebar"
        :items="menuItems"
        :collapsed="collapsed && !isMobile"
        :is-mobile="isMobile"
        :company-logo-url="companyLogoUrl"
        :app-version="appVersion"
        :show-menu="showAdminMenu"
        @go-home="goHome"
        @toggle-sidebar="toggleSidebar"
        @toggle-item="toggleItem"
        @navigate="changeRoute"
        @flyout="onFlyout"
      />
    </v-navigation-drawer>

    <v-toolbar
      v-if="canShowShell"
      color="white"
      app
      fixed
      clipped-left
      flat
      class="app_shell__toolbar"
      height="70"
    >
      <app-topbar
        :user-email="userEmail"
        :user-initials="userInitials"
        :user-role="userRoleLabel"
        :sidebar-open="drawerVisible"
        :collapsed="collapsed && !isMobile"
        :is-mobile="isMobile"
        :show-logo="collapsed && !isMobile"
        :company-logo-url="companyLogoUrl"
        @toggle-sidebar="toggleSidebar"
        @go-home="goHome"
        @logout="logout"
      />
    </v-toolbar>

    <div
      v-if="flyout && flyout.item"
      class="app_sidebar__flyout"
      :class="{ 'app_sidebar__flyout--tip': !flyout.item.dropdown }"
      :style="flyout.style"
      @mouseenter="onFlyoutEnter"
      @mouseleave="onFlyoutLeave"
      @click.stop
    >
      <div class="app_sidebar__flyout-head">
        <span class="app_sidebar__flyout-head-title">{{ flyout.item.title }}</span>
      </div>
      <div
        v-if="flyout.item.dropdown && flyout.item.sub_items && flyout.item.sub_items.length"
        class="app_sidebar__flyout-divider"
        aria-hidden="true"
      />
      <ul
        v-if="flyout.item.dropdown && flyout.item.sub_items && flyout.item.sub_items.length"
        class="app_sidebar__flyout-list"
      >
        <li
          v-for="(subItem, j) in flyout.item.sub_items"
          :key="'flyout-sub-' + j"
          class="app_sidebar__flyout-item"
          :class="{
            'app_sidebar__flyout-item--active': subItem.selected,
            'app_sidebar__flyout-item--inactive': flyout.item.active && !subItem.active
          }"
          @click="navigateFromFlyout(subItem, subItem.route)"
        >
          {{ subItem.title }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import AppSidebar from './AppSidebar.vue'
import AppTopbar from './AppTopbar.vue'
import {
  buildMenuItems,
  filterMenuForUserType,
  syncMenuSelection
} from './menuConfig'

function enabledItem(companyObject) {
  if (!companyObject) return false
  return Object.entries(companyObject).filter(
    ([, value]) => value !== '' && value === true
  ).length > 0
}

export default {
  name: 'AppShell',
  components: {
    AppSidebar,
    AppTopbar
  },
  data: () => ({
    drawer: true,
    collapsed: false,
    isMobile: false,
    items: [],
    flyout: null
  }),
  computed: {
    drawerVisible: {
      get() {
        if (this.isMobile) return this.drawer
        return true
      },
      set(value) {
        if (this.isMobile) this.drawer = value
      }
    },
    canShowShell() {
      const type = this.getUserType && this.getUserType()
      return (
        type === 'Admin' ||
        type === 'Counter' ||
        type === 'Counter_offline' ||
        type === 'Booking_office' ||
        type === 'CounterPrinter'
      )
    },
    showAdminMenu() {
      const type = this.getUserType && this.getUserType()
      return type !== 'Booking_office' && type !== 'CounterPrinter'
    },
    sidebarWidth() {
      if (this.isMobile) return 280
      return this.collapsed ? 80 : 280
    },
    appVersion() {
      return process.env.APP_VERSION || 'dev'
    },
    menuItems() {
      const type = this.getUserType ? this.getUserType() : ''
      return filterMenuForUserType(this.items, type)
    },
    companyLogoUrl() {
      const company = this.getCompany && this.getCompany()
      const logo = company && company.personalize && company.personalize.app_logo
      return logo && logo.url ? logo.url : ''
    },
    userEmail() {
      return this.getEmailUser ? String(this.getEmailUser() || '').trim() : ''
    },
    userInitials() {
      const source = this.getDisplayUserName() || this.userEmail
      if (!source) return '?'
      const parts = source.trim().split(/\s+/).filter(Boolean)
      if (parts.length >= 2) {
        return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase()
      }
      return source.slice(0, 2).toUpperCase()
    },
    userRoleLabel() {
      const type = this.getUserType ? this.getUserType() : ''
      if (type === 'Admin') return 'Administrador'
      if (type === 'Counter' || type === 'Counter_offline') return 'Counter'
      if (type === 'Booking_office') return 'Oficina de reservas'
      if (type === 'CounterPrinter') return 'Impresión'
      return type || 'Usuario'
    }
  },
  watch: {
    $route(to) {
      syncMenuSelection(this.items, to && to.name)
      this.flyout = null
      if (this.$refs.sidebar && this.$refs.sidebar.closeFlyout) {
        this.$refs.sidebar.closeFlyout()
      }
      if (this.isMobile) this.drawer = false
    }
  },
  created() {
    this.updateMobileState(true)
    window.addEventListener('resize', this.onMobileResize)
  },
  beforeMount() {
    this.items = buildMenuItems({
      getCompany: () => this.getCompany(),
      enabledItem
    }).map((item) => Object.assign({}, item, {
      expanded: !!item.expanded,
      selected: !!item.selected,
      sub_items: (item.sub_items || []).map((sub) => Object.assign({}, sub))
    }))
  },
  mounted() {
    syncMenuSelection(this.items, this.$route && this.$route.name)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onMobileResize)
  },
  methods: {
    getDisplayUserName() {
      const name = this.getNameUser ? String(this.getNameUser() || '').trim() : ''
      if (name) return name
      return this.getEmailUser ? String(this.getEmailUser() || '').trim() : ''
    },
    goHome() {
      if (this.$route.name === 'main_menu_page') return
      this.$router.push({ name: 'main_menu_page' })
    },
    toggleSidebar() {
      if (this.isMobile) {
        this.drawer = !this.drawer
        return
      }
      this.collapsed = !this.collapsed
      if (!this.collapsed) this.flyout = null
    },
    toggleItem(item) {
      if (!item.active) return
      this.$set(item, 'expanded', !item.expanded)
    },
    onFlyout(payload) {
      this.flyout = payload
    },
    onFlyoutEnter() {
      if (this.$refs.sidebar && this.$refs.sidebar.onFlyoutEnter) {
        this.$refs.sidebar.onFlyoutEnter()
      }
    },
    onFlyoutLeave() {
      if (this.$refs.sidebar && this.$refs.sidebar.onFlyoutLeave) {
        this.$refs.sidebar.onFlyoutLeave()
      }
    },
    navigateFromFlyout(subItem, route) {
      this.flyout = null
      if (this.$refs.sidebar && this.$refs.sidebar.closeFlyout) {
        this.$refs.sidebar.closeFlyout()
      }
      this.changeRoute(subItem, route)
    },
    changeRoute(item, link) {
      if (!item || !item.active) return
      this.flyout = null
      if (this.$refs.sidebar && this.$refs.sidebar.closeFlyout) {
        this.$refs.sidebar.closeFlyout()
      }
      if (this.isMobile) this.drawer = false
      const routeName = item.active ? link : ''
      if (!routeName) return
      this.$router.push({ name: routeName })
    },
    updateMobileState(setInitialDrawer) {
      const mobile = window.matchMedia('(max-width: 639px)').matches
      this.isMobile = mobile
      if (mobile) {
        this.collapsed = false
        if (setInitialDrawer) this.drawer = false
      } else if (setInitialDrawer) {
        this.drawer = true
      }
    },
    onMobileResize() {
      const wasMobile = this.isMobile
      this.updateMobileState(false)
      if (wasMobile && !this.isMobile) {
        this.drawer = true
      } else if (!wasMobile && this.isMobile) {
        this.drawer = false
        this.collapsed = false
      }
    },
    logout() {
      if (!window.eel) {
        window.location = '/'
      } else {
        window.eel.close_program()
        window.close()
        window.location = '/'
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/layout/AppShell.vue